<script lang="ts" setup>
  import { cn } from '@/lib/utils'
  import NumberFlow from '@number-flow/vue'
  import { computed, onBeforeUnmount, ref, useSlots, watch } from 'vue'
  import { getLoadingStepsArray } from '~/composables/map/config/progressiveLoadingConfig'

  const props = withDefaults(
    defineProps<{
      class?: string
      height?: number
    }>(),
    {
      class: '',
      height: 34
    }
  )

  const { locale, t } = useI18n()
  const mapStore = useMapStore()
  const { isLoadingAssets, isLoadingIds, isProgressiveLoading, currentLoadingStep, overallLoadingProgress } =
    storeToRefs(mapStore)

  const open = ref(false)
  const slots = useSlots()

  const percentageValue = ref(0)
  const displayPercentage = ref(0) // For smooth display with creeping progress
  const progressInterval = ref<ReturnType<typeof setInterval> | null>(null)
  const creepInterval = ref<ReturnType<typeof setInterval> | null>(null)
  const isVisibleWithMinDuration = ref(false)
  const minDisplayTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
  const showDelayTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
  const showStartTime = ref<number>(0)
  const wasActuallyShown = ref(false)

  const isSlotAvailable = computed(() => !!slots.default)
  const borderRadius = computed(() => `${props.height / 2}px`)

  const isLoading = computed(() => isLoadingAssets.value || isLoadingIds.value || isProgressiveLoading.value)

  const isLoadingIdsOnly = computed(() => isLoadingIds.value && !isLoadingAssets.value)

  const title = computed(() => {
    if (isProgressiveLoading.value && currentLoadingStep.value > 0) {
      const steps = getLoadingStepsArray()
      const stepIndex = currentLoadingStep.value - 1

      if (stepIndex === steps.length - 1) {
        return t('map.loading.finishingUp')
      }

      return t('map.loading.loadingMapData')
    }
    if (isLoadingAssets.value) {
      return t('map.loadingAssets')
    }
    if (isLoadingIds.value) {
      return t('map.applyingFilters')
    }
    return t('map.applyingFilters')
  })

  /**
   * Calculate smart percentage targets for each loading step
   * Uses psychological milestones based on real-world asset distribution
   * Most licenses have assets concentrated in city-level areas
   */
  const calculateStepPercentageTargets = (): number[] => {
    const steps = getLoadingStepsArray()
    const targets: number[] = []

    // Define psychological milestones based on geohash levels
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i]

      if (step.level === null) {
        // Final step always reaches 100%
        targets.push(100)
      } else if (step.level === 6) {
        // Detailed view (block/neighborhood) - quick initial feedback
        targets.push(18)
      } else if (step.level === 5) {
        // Medium detail (small city) - significant milestone
        targets.push(52)
      } else if (step.level === 4) {
        // Overview (city area) - "almost done" psychology (90%)
        targets.push(90)
      } else {
        // Fallback: exponential distribution for unknown levels
        const progress = (i + 1) / steps.length
        targets.push(Math.round(Math.pow(progress, 0.7) * 100))
      }
    }

    return targets
  }

  const stepTargets = ref<number[]>(calculateStepPercentageTargets())

  /**
   * Start creeping progress - slowly increment toward target
   * Gives feeling of continuous progress during network waits
   */
  const startCreepingProgress = (currentTarget: number): void => {
    if (creepInterval.value) {
      clearInterval(creepInterval.value)
    }

    const startValue = displayPercentage.value
    const targetValue = currentTarget - 1 // Stop 1% before target

    if (startValue >= targetValue) {
      return
    }

    let elapsed = 0
    const duration = 2500 // 2.5 seconds to creep to target
    const updateInterval = 50 // Update every 50ms

    creepInterval.value = setInterval(() => {
      elapsed += updateInterval
      const progress = Math.min(elapsed / duration, 1)

      // Easing: fast at start, slow near end (cubic ease-out)
      const eased = 1 - Math.pow(1 - progress, 3)
      const newValue = startValue + (targetValue - startValue) * eased

      displayPercentage.value = Math.min(newValue, targetValue)

      if (progress >= 1) {
        if (creepInterval.value) {
          clearInterval(creepInterval.value)
        }
        creepInterval.value = null
      }
    }, updateInterval)
  }

  const clearTimers = (): void => {
    if (progressInterval.value) {
      clearInterval(progressInterval.value)
      progressInterval.value = null
    }
    if (creepInterval.value) {
      clearInterval(creepInterval.value)
      creepInterval.value = null
    }
    if (minDisplayTimeout.value) {
      clearTimeout(minDisplayTimeout.value)
      minDisplayTimeout.value = null
    }
    if (showDelayTimeout.value) {
      clearTimeout(showDelayTimeout.value)
      showDelayTimeout.value = null
    }
  }

  const startProgress = (): void => {
    clearTimers()
    stepTargets.value = calculateStepPercentageTargets()

    // For progressive loading, use smart progress with creeping
    if (isProgressiveLoading.value) {
      const stepIndex = currentLoadingStep.value - 1
      if (stepIndex >= 0 && stepIndex < stepTargets.value.length) {
        const target = stepTargets.value[stepIndex]
        percentageValue.value = target / 100
        // Start from previous step's target or 0
        displayPercentage.value = stepIndex > 0 ? stepTargets.value[stepIndex - 1] : 0
        startCreepingProgress(target)
      }
    } else {
      // For non-progressive loading, use simulated progress
      percentageValue.value = 0
      displayPercentage.value = 0
      let currentProgress = 0
      progressInterval.value = setInterval(() => {
        // Slow down as we approach higher percentages
        const increment = Math.random() * (90 - currentProgress) * 0.1
        currentProgress = Math.min(currentProgress + increment, 90)
        percentageValue.value = currentProgress / 100
        displayPercentage.value = currentProgress

        // Stop at 90% to wait for actual completion
        if (currentProgress >= 90) {
          if (progressInterval.value) {
            clearInterval(progressInterval.value)
          }
          progressInterval.value = null
        }
      }, 100)
    }
  }

  const completeProgress = (): void => {
    clearTimers()
    percentageValue.value = 1
    displayPercentage.value = 100

    // Reset values after a delay to allow exit animation
    setTimeout(() => {
      percentageValue.value = 0
      displayPercentage.value = 0
    }, 700)
  }

  // Watch for loading state changes with debounce delay and minimum display duration
  watch(
    isLoading,
    (newIsLoading, oldIsLoading) => {
      if (newIsLoading && !oldIsLoading) {
        // Started loading - wait 150ms before showing loader (debounce for quick operations)
        clearTimers()
        wasActuallyShown.value = false

        const showDelay = 150 // Only show loader if loading takes longer than this

        showDelayTimeout.value = setTimeout(() => {
          // Loading is still ongoing after delay - show the loader
          isVisibleWithMinDuration.value = true
          wasActuallyShown.value = true
          showStartTime.value = Date.now()
          startProgress()
        }, showDelay)
      } else if (!newIsLoading && oldIsLoading) {
        // Finished loading
        clearTimers()

        // If loader was never shown (completed before delay), do nothing
        if (!wasActuallyShown.value) {
          isVisibleWithMinDuration.value = false
          return
        }

        // If loader was shown, enforce minimum display duration
        const elapsed = Date.now() - showStartTime.value
        const minDuration = 500 // Minimum display time in ms
        const remainingTime = Math.max(0, minDuration - elapsed)

        minDisplayTimeout.value = setTimeout(() => {
          isVisibleWithMinDuration.value = false
          completeProgress()
          wasActuallyShown.value = false
        }, remainingTime)
      }
    },
    { immediate: true }
  )

  // Watch for progressive loading step changes
  watch([currentLoadingStep, overallLoadingProgress], ([newStep]) => {
    if (isProgressiveLoading.value && isLoading.value && newStep > 0) {
      const stepIndex = newStep - 1
      if (stepIndex >= 0 && stepIndex < stepTargets.value.length) {
        const target = stepTargets.value[stepIndex]
        percentageValue.value = target / 100

        // Start creeping from current display value to new target
        startCreepingProgress(target)
      }
    }
  })

  // Watch for when step actually completes to jump to target
  watch(overallLoadingProgress, (newProgress) => {
    if (isProgressiveLoading.value && isLoading.value) {
      const stepIndex = currentLoadingStep.value - 1
      if (stepIndex >= 0 && stepIndex < stepTargets.value.length) {
        const target = stepTargets.value[stepIndex]
        // When actual progress reaches or exceeds target, jump to it
        if (newProgress >= target) {
          displayPercentage.value = target
        }
      }
    }
  })

  onBeforeUnmount(() => {
    clearTimers()
  })
</script>

<template>
  <Transition
    enter-active-class="loader-enter-active"
    enter-from-class="loader-enter-from"
    enter-to-class="loader-enter-to"
    leave-active-class="loader-leave-active"
    leave-from-class="loader-leave-from"
    leave-to-class="loader-leave-to"
  >
    <div
      v-if="isVisibleWithMinDuration"
      :class="
        cn(
          'fixed left-1/2 top-5 z-[999] -translate-x-1/2 bg-white/90 backdrop-blur-lg shadow-lg border-radius cursor-default overflow-hidden text-primary w-fit transition-all duration-300',
          $props.class
        )
      "
      :style="{ minWidth: '260px', maxWidth: open && isSlotAvailable ? '360px' : '320px' }"
    >
      <header :style="{ height: `${props.height}px` }" class="flex cursor-default items-center gap-2 px-4">
        <!-- Show lighter spinner when loading IDs only -->
        <div
          v-if="isLoadingIdsOnly"
          class="inline-block h-4 w-4 animate-spin rounded-full border-[1.5px] border-solid border-current border-e-transparent align-[-0.125em] text-primary/60 motion-reduce:animate-[spin_1.5s_linear_infinite]"
          role="status"
        >
          <span
            class="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]"
          ></span>
        </div>
        <!-- Show circular progress bar always (not just when loading assets) -->
        <AnimatedCircularProgressBar
          v-if="!isLoadingIdsOnly"
          :circle-stroke-width="10"
          :duration="0.3"
          :max="100"
          :min="0"
          :show-percentage="false"
          :value="displayPercentage"
          class="w-5"
          gauge-primary-color="#0A2538"
          gauge-secondary-color="#6b728099"
        />
        <h1
          :class="
            cn('grow pt-1 text-center text-sm font-semibold', isLoadingIdsOnly ? 'text-primary/80' : 'text-primary')
          "
        >
          {{ title }}
        </h1>
        <!-- Only show percentage number when loading assets (not IDs) -->
        <NumberFlow
          v-if="!isLoadingIdsOnly"
          :format="{
            style: 'percent'
          }"
          :locales="locale"
          :value="displayPercentage / 100"
          class="text-sm text-primary"
        />
      </header>
      <div
        v-if="isSlotAvailable"
        :style="{ maxHeight: open ? '240px' : '0px', opacity: open ? 1 : 0 }"
        class="mb-2 flex flex-col gap-1 overflow-y-auto px-4 text-sm transition-all duration-300"
      >
        <slot />
      </div>
    </div>
  </Transition>
</template>

<style scoped>
  .border-radius {
    border-radius: v-bind(borderRadius);
  }

  /* Entrance animation with elegant soft bounce */
  .loader-enter-active {
    animation: slideDownBounce 600ms cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .loader-enter-from {
    opacity: 0;
  }

  .loader-enter-to {
    opacity: 1;
  }

  /* Exit animation - smooth fade up */
  .loader-leave-active {
    animation: slideUpFade 400ms cubic-bezier(0.4, 0, 0.6, 1);
  }

  .loader-leave-from {
    opacity: 1;
  }

  .loader-leave-to {
    opacity: 0;
  }

  /* Keyframe for entrance: slide down from top with soft bounce */
  @keyframes slideDownBounce {
    0% {
      transform: translate(-50%, -120%);
      opacity: 0;
    }
    70% {
      transform: translate(-50%, -8%);
      opacity: 1;
    }
    85% {
      transform: translate(-50%, 12%);
    }
    100% {
      transform: translate(-50%, 0);
      opacity: 1;
    }
  }

  /* Keyframe for exit: smooth slide up with fade */
  @keyframes slideUpFade {
    0% {
      transform: translate(-50%, 0);
      opacity: 1;
    }
    100% {
      transform: translate(-50%, -40%);
      opacity: 0;
    }
  }
</style>
